import { AnimatePresence, motion } from "framer-motion";
import { useToasts, type ToastKind } from "@/state/toast";
import { tokens } from "@/art/tokens";

/**
 * 토스트 톤 — 테두리는 어두운 시맨틱 토큰, 타이틀은 그 톤을 어두운 배경 위에서 읽히게
 * 밝힌 변형. 이 4개 파스텔은 tokens 의 moss/brass/burgundy 와 의미적으로 연결된
 * "라이트 변형" 이다 — 토큰에 추가하지 않고 여기에만 쓰는 까닭은 토스트 단독 목적.
 */
const toneFor: Record<ToastKind, { border: string; title: string; glow: string; bar: string }> = {
    info:    { border: tokens.color.steel,    title: tokens.color.parchmentDim, glow: "rgba(74,85,104,0.35)",  bar: tokens.color.steel },
    success: { border: tokens.color.moss,     title: "#A3BFA3",                 glow: "rgba(62,90,58,0.45)",   bar: tokens.color.moss },
    warning: { border: tokens.color.brass,    title: "#D9BB8A",                 glow: "rgba(169,123,61,0.45)", bar: tokens.color.brass },
    error:   { border: tokens.color.burgundy, title: "#E89AA0",                 glow: "rgba(92,31,46,0.50)",   bar: tokens.color.burgundy },
};

/**
 * 화면에 동시 노출되는 토스트 최대 개수. 이보다 많이 쌓이면 "+N 더 쌓임" 인디케이터만 노출.
 * 3개로 정한 이유: 460px 폭 × 최대 3개면 상단에서 hud 영역을 절반 이하로 먹음.
 * 2026-04-22 E21: 기존에는 전부 쌓여 가독성 붕괴. 오래된 것부터 cap out.
 */
const MAX_VISIBLE = 3;

export function ToastRail() {
    const entries = useToasts((s) => s.entries);
    const dismiss = useToasts((s) => s.dismiss);
    const clear = useToasts((s) => s.clear);

    // 최신 3개만 노출. 오래된 것은 자동 TTL 로 사라지거나, "더 보기" 클릭 시 일괄 dismiss.
    const visible = entries.slice(-MAX_VISIBLE);
    const overflowCount = Math.max(0, entries.length - visible.length);

    return (
        <div className="fixed top-l right-l z-overlay flex flex-col gap-s pointer-events-none" style={{ width: 460 }}>
            <AnimatePresence>
                {visible.map((entry) => {
                    const tone = toneFor[entry.kind];
                    return (
                        <motion.div
                            key={entry.id}
                            initial={{ opacity: 0, x: 40, scale: 0.96 }}
                            animate={entry.kind === "error"
                                ? {
                                    opacity: 1,
                                    x: 0,
                                    scale: 1,
                                    boxShadow: [
                                        `0 0 20px ${tone.glow}, 0 8px 20px ${tokens.color.ink}88, inset 0 1px 0 ${tone.bar}22`,
                                        `0 0 32px ${tone.glow}, 0 10px 24px ${tokens.color.ink}88, inset 0 1px 0 ${tone.bar}44`,
                                        `0 0 20px ${tone.glow}, 0 8px 20px ${tokens.color.ink}88, inset 0 1px 0 ${tone.bar}22`,
                                    ],
                                }
                                : { opacity: 1, x: 0, scale: 1 }
                            }
                            exit={{ opacity: 0, x: 40, scale: 0.98, transition: { duration: 0.25 } }}
                            transition={entry.kind === "error"
                                ? { duration: 0.4, ease: tokens.easing.decelerate, boxShadow: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }
                                : { duration: 0.4, ease: tokens.easing.decelerate }
                            }
                            whileHover={{ scale: 1.015, x: -2 }}
                            onClick={() => dismiss(entry.id)}
                            className="relative overflow-hidden rounded-m cursor-pointer pointer-events-auto"
                            style={{
                                background: `linear-gradient(180deg, ${tokens.color.ink}F4 0%, ${tokens.color.ink}EA 100%)`,
                                border: `1px solid ${tone.border}AA`,
                                boxShadow: `0 0 24px ${tone.glow}, 0 8px 20px ${tokens.color.ink}88, inset 0 1px 0 ${tone.bar}22`,
                            }}
                            title="클릭하면 닫힙니다"
                        >
                            {/* 2026-04-23: 좌측 3px accent 스트립 — 인지성 향상. */}
                            <div
                                aria-hidden
                                className="absolute inset-y-0 left-0"
                                style={{
                                    width: 3,
                                    background: `linear-gradient(180deg, ${tone.bar}EE 0%, ${tone.bar}66 100%)`,
                                    boxShadow: `0 0 8px ${tone.glow}`,
                                }}
                            />
                            <div className="p-m pl-[18px]">
                                {entry.count > 1 && (
                                    <div
                                        className="absolute right-s top-s rounded-pill px-xs py-[2px] font-display"
                                        style={{
                                            color: tokens.color.parchment,
                                            background: `${tone.bar}2A`,
                                            border: `1px solid ${tone.bar}66`,
                                            fontSize: 11,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                        }}
                                    >
                                        ×{entry.count}
                                    </div>
                                )}
                                {entry.title && (
                                    <div
                                        className="font-display mb-xs"
                                        style={{
                                            color: tone.title,
                                            fontSize: tokens.type.size.small,
                                            letterSpacing: "0.04em",
                                            textShadow: `0 0 10px ${tone.glow}`,
                                        }}
                                    >
                                        {entry.title}
                                    </div>
                                )}
                                <div
                                    className="font-body"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.small,
                                        lineHeight: tokens.type.lineHeight.snug,
                                    }}
                                >
                                    {entry.message}
                                </div>
                            </div>
                            {/*
                             * 하단 TTL 프로그레스 바 — 남은 시간을 시각화. motion.width 가 0 으로
                             * 줄어들면서 사라짐 타이밍을 암시. ttlMs 가 이미 state 에 있으니 재활용.
                             */}
                            <motion.div
                                aria-hidden
                                className="absolute bottom-0 left-0 h-[2px] rounded-b-m"
                                initial={{ width: "100%" }}
                                animate={{ width: "0%" }}
                                transition={{ duration: entry.ttlMs / 1000, ease: "linear" }}
                                style={{ background: `linear-gradient(90deg, ${tone.bar}FF, ${tone.bar}66)`, boxShadow: `0 0 8px ${tone.glow}` }}
                            />
                        </motion.div>
                    );
                })}
            </AnimatePresence>
            {overflowCount > 0 && (
                <motion.button
                    type="button"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 40 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => {
                        // 오래된 것들을 한 번에 밀어 냄 — 화면 단속.
                        const older = entries.slice(0, entries.length - MAX_VISIBLE);
                        older.forEach((e) => dismiss(e.id));
                    }}
                    className="rounded-pill px-m py-xs pointer-events-auto self-end"
                    style={{
                        background: `${tokens.color.ink}E0`,
                        border: `1px solid ${tokens.color.parchmentDim}55`,
                        color: tokens.color.parchmentDim,
                        fontSize: tokens.type.size.tiny,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        cursor: "pointer",
                    }}
                    title="밀려 있는 알림 전부 닫기"
                >
                    ＋ 오래된 알림 {overflowCount}개 · 한 번에 정리
                </motion.button>
            )}
            {entries.length >= 4 && (
                <button
                    type="button"
                    onClick={() => clear()}
                    className="rounded-pill px-s py-[2px] pointer-events-auto self-end"
                    style={{
                        background: "transparent",
                        border: `1px dashed ${tokens.color.parchmentDim}44`,
                        color: tokens.color.parchmentDim,
                        fontSize: 11,
                        opacity: 0.75,
                        cursor: "pointer",
                    }}
                    title="모든 알림 정리"
                >
                    전부 지우기
                </button>
            )}
        </div>
    );
}
